import { FormikProps } from 'formik';
import React, { MutableRefObject, useCallback, useMemo, useState, VFC } from 'react';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { JourneyFormik, JourneyForm } from 'src/common/JourneyFormik';
import JourneySlideover, { DEFAULT_ANIMATION_DURATION } from 'src/common/JourneySlideOver';
import { apiUpdateUserPassword } from 'src/utils/journeyApi';
import { useReactConstructor } from 'src/utils/react/constructor.hook';
import * as Yup from 'yup';

import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';

interface DashboardSettingsAccountChangePasswordPanelProps {
  openRef: MutableRefObject<() => void>;
  onSuccess?: (user: OrganizationUser) => void;
}

const validationSchema = Yup.object({
  current_password: Yup.string().required('Required'),
  new_password: Yup.string().required('Required'),
});

export const DashboardSettingsAccountChangePasswordPanel: VFC<DashboardSettingsAccountChangePasswordPanelProps> = ({
  openRef,
  onSuccess,
}) => {
  const [mountPanel, setMountPanel] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const { setErrorNotification, setSuccessNotification } = useNotificationMessage();

  const onPanelClosed = useCallback(() => {
    setShowPanel(false);
    setTimeout(() => {
      setMountPanel(false);
    }, DEFAULT_ANIMATION_DURATION);
  }, []);

  useReactConstructor(() => {
    openRef.current = () => {
      setMountPanel(true);
      setTimeout(() => {
        setShowPanel(true);
      }, 300);
    };
  });

  const formikParams = useMemo(
    () => ({
      initialValues: { current_password: '', new_password: '' },
      validationSchema,
      onSubmit: (values: Record<string, string>) => {
        return apiUpdateUserPassword(values)
          .then((user: OrganizationUser) => {
            onSuccess && onSuccess(user);
            onPanelClosed();
            setSuccessNotification('Password updated successfully');
          })
          .catch((error: any) => {
            setErrorNotification(error.message);
          });
      },
    }),
    [onPanelClosed, onSuccess, setErrorNotification, setSuccessNotification]
  );

  return (
    <>
      {mountPanel && (
        <JourneySlideover
          isBedrock
          title='Change password'
          open={showPanel}
          maxWidth='auto'
          panelClassName='w-[368px]'
          titleClassName='font-brand-serif'
          onClose={onPanelClosed}
        >
          <JourneyFormik {...formikParams}>
            {({ values }: FormikProps<Record<string, string>>) => {
              const isFormValid = validationSchema.isValidSync(values);

              return (
                <JourneyForm className='flex flex-col space-y-4'>
                  <FormikTextInput
                    id='current-password-input'
                    required
                    name='current_password'
                    type='password'
                    label='Current password'
                  />
                  <FormikTextInput
                    id='new-password-input'
                    required
                    name='new_password'
                    type='password'
                    label='New password'
                  />
                  <button type='submit' className='flex btn-bedrock-primary justify-center' disabled={!isFormValid}>
                    Save new password
                  </button>
                </JourneyForm>
              );
            }}
          </JourneyFormik>
        </JourneySlideover>
      )}
    </>
  );
};
